import type React from "react";
import { useState, useRef, useEffect } from "react";
import { X, ChevronDown } from "lucide-react";
import "./MultiSelectDropdown.css";

interface Option {
    value: string
    label: string
}

interface MultiSelectProps {
    label?: string
    required?: boolean
    options: Option[]
    selectedValues: string[]
    onChange: (selectedValues: string[]) => void
    placeholder?: string
    maxDisplayItems?: number
    disabled?: boolean
}

const MultiSelectDropdown: React.FC<MultiSelectProps> = ({
    label,
    required = false,
    options,
    selectedValues,
    onChange,
    placeholder = "Select options...",
    maxDisplayItems = 2,
    disabled = false,
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    const handleToggle = () => {
        if (disabled) return
        setIsOpen(!isOpen)
    }

    const handleSelect = (value: string) => {
        if (disabled) return
        if (selectedValues.includes(value)) {
            onChange(selectedValues.filter((v) => v !== value))
        } else {
            onChange([...selectedValues, value])
        }
    }

    const handleRemove = (value: string, e: React.MouseEvent) => {
        e.stopPropagation()
        if (disabled) return
        onChange(selectedValues.filter((v) => v !== value))
    }

    const getDisplayItems = () => {
        const displayItems = selectedValues.slice(0, maxDisplayItems)
        const remainingCount = selectedValues.length - maxDisplayItems
        return { displayItems, remainingCount }
    }

    const { displayItems, remainingCount } = getDisplayItems()

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => {
            document.removeEventListener("mousedown", handleClickOutside)
        }
    }, [])

    return (
        <div className="multi-select-container customDropdown" ref={dropdownRef}>
            {label && (
                <label className="multi-select-label">
                    {label}
                    {required && <span className="text-danger">*</span>}
                </label>
            )}

            <div
                className={`multi-select-input ${isOpen ? "open" : ""} ${disabled ? "disabled" : ""}`}
                onClick={handleToggle}
                aria-disabled={disabled}
            >
                <div className="selected-items">
                    {selectedValues.length === 0 ? (
                        <span className="placeholder">{placeholder}</span>
                    ) : (
                        <>
                            {displayItems.map((value) => {
                                const option = options.find((opt) => opt.value === value)
                                return (
                                    <span key={value} className="selected-item badge-primary">
                                        <span
                                            className="selected-item-text"
                                            title={option?.label || value}
                                        >
                                            {option?.label || value}
                                        </span>
                                        <button
                                            type="button"
                                            className="remove-btn"
                                            onClick={(e) => handleRemove(value, e)}
                                            aria-label={`Remove ${option?.label || value}`}
                                            disabled={disabled}
                                        >
                                            <X size={12} />
                                        </button>
                                    </span>
                                )
                            })}
                            {remainingCount > 0 && (
                                <span className="more-items badge-secondary">
                                    +{remainingCount} more
                                    <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            if (disabled) return
                                            // Remove all remaining items
                                            onChange(selectedValues.slice(0, maxDisplayItems))
                                        }}
                                        aria-label="Remove remaining items"
                                        disabled={disabled}
                                    >
                                        <X size={12} />
                                    </button>
                                </span>
                            )}
                        </>
                    )}
                </div>

                <ChevronDown size={16} className={`dropdown-arrow ${isOpen ? "rotated" : ""}`} />
            </div>

            {isOpen && (
                <div className="dropdown-menu-custom">
                    {options.map((option) => (
                        <div
                            key={option.value}
                            className={`dropdown-item-custom ${selectedValues.includes(option.value) ? "selected" : ""}`}
                            onClick={() => handleSelect(option.value)}
                        >
                            <label className="checkbox-label">
                                <input
                                    type="checkbox"
                                    checked={selectedValues.includes(option.value)}
                                    onChange={() => { }} // Handled by parent onClick
                                    className="checkbox-input"
                                />
                                <span className="checkbox-text">{option.label}</span>
                            </label>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

export default MultiSelectDropdown;
